import type {
  CatalogAuditRecord,
  CatalogProduct,
  CatalogStatus,
  CreateCategoryInput,
  CreateProductInput,
  DuplicateProductInput,
  PaginatedResult,
  ProductCategory,
  UpdateCategoryInput,
  UpdateProductInput,
} from '@emazy/shared-types';

export type AdminCategoryListInput = {
  status: CatalogStatus;
  limit?: number;
  cursor?: string;
};

export type AdminProductListInput = AdminCategoryListInput & {
  categoryId?: string;
  exactSku?: string;
  exactSlug?: string;
};

export type AuditListInput = { limit?: number; cursor?: string };
export type CatalogLifecycleInput = { expectedVersion: number };

export interface AdminCatalogApi {
  listCategories(
    input: AdminCategoryListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<ProductCategory>>;
  getCategory(id: string, signal?: AbortSignal): Promise<ProductCategory>;
  createCategory(input: CreateCategoryInput): Promise<ProductCategory>;
  updateCategory(id: string, input: UpdateCategoryInput): Promise<ProductCategory>;
  publishCategory(id: string, input: CatalogLifecycleInput): Promise<ProductCategory>;
  archiveCategory(id: string, input: CatalogLifecycleInput): Promise<ProductCategory>;
  listCategoryAudit(
    id: string,
    input?: AuditListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<CatalogAuditRecord>>;
  listProducts(
    input: AdminProductListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<CatalogProduct>>;
  getProduct(id: string, signal?: AbortSignal): Promise<CatalogProduct>;
  createProduct(input: CreateProductInput): Promise<CatalogProduct>;
  updateProduct(id: string, input: UpdateProductInput): Promise<CatalogProduct>;
  publishProduct(id: string, input: CatalogLifecycleInput): Promise<CatalogProduct>;
  archiveProduct(id: string, input: CatalogLifecycleInput): Promise<CatalogProduct>;
  duplicateProduct(id: string, input: DuplicateProductInput): Promise<CatalogProduct>;
  listProductAudit(
    id: string,
    input?: AuditListInput,
    signal?: AbortSignal,
  ): Promise<PaginatedResult<CatalogAuditRecord>>;
}

export type AdminCatalogErrorCode =
  | 'validation-error'
  | 'version-conflict'
  | 'category-slug-conflict'
  | 'product-slug-conflict'
  | 'sku-conflict'
  | 'category-not-empty'
  | 'not-publishable'
  | 'invalid-lifecycle'
  | 'not-found'
  | 'forbidden'
  | 'network-error'
  | 'service-unavailable'
  | 'unknown';

export class AdminCatalogError extends Error {
  constructor(
    readonly code: AdminCatalogErrorCode,
    message: string,
    readonly status?: number,
    readonly fieldErrors?: Record<string, string[]>,
    readonly requestId?: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'AdminCatalogError';
  }
}
